import { Injectable, OnDestroy }           from '@angular/core';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { Subscription }                    from 'rxjs';
import { TranslateService }                from '@ngx-translate/core';

export interface CSVParserOptions {
  delimiter?: string;
  header   ?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class CSVService implements OnDestroy {
  private _defaultOptions : CSVParserOptions = {
    delimiter: ',',
    header   : false
  };

  subscription: Subscription;

  constructor (
    private csvParser       : NgxCsvParser,
    private translateService: TranslateService
  ) {}

  isCSV ( file : File ) : boolean {
    return file.type === 'text/csv'
      || file.name.toLowerCase().endsWith('.csv');
  }

  ngOnDestroy () : void {
    if ( this.subscription ) {
      this.subscription.unsubscribe();
    }
  }

  parse ( data : File | string, options : CSVParserOptions = {} ) : ng.IPromise<Array<Array<string>>> {
    options = Object.assign({}, this._defaultOptions, options);

    return new Promise((resolve, reject) => {
      if ( typeof data === 'string' ) {
         resolve(this.parseString(data as string, options));
      }
      else {
        this.parseFile(data as File, options)
        .then(( csvArray :  Array<Array<string>> ) => resolve(csvArray))
        .catch(error => reject(error));
      }
    });
  }

  parseFile ( file : File, options : CSVParserOptions = {} ) : ng.IPromise<Array<Array<string>>> {
    options = Object.assign({}, this._defaultOptions, options);

    return new Promise((resolve, reject) => {
      if ( this.isCSV(file) ) {
        this.subscription = this.csvParser.parse(file, options)
        .subscribe({
          next: ( rows : any ) => {
            const csvArray = ( rows as Array<Array<string>> ).map(r => r.map(v => (v ?? '').toString()));

            resolve(csvArray);
          },
          error: ( error : NgxCSVParserError ) => {
            reject(this.translateService.instant('JS_SPACE.IMPORTS.ERROR_READING_FILE'));
          }
        });
      }
      else {
        reject(this.translateService.instant('JS_SPACE.IMPORTS.INVALID_FILE_TYPE'));
      }
    });
  }

  parseString ( data : string, options : CSVParserOptions = {} ) : Array<Array<string>> {
    options = Object.assign({}, this._defaultOptions, options);

    return this.csvParser.csvStringToArray(data, options.delimiter).map(r => r.map(v => (v ?? '').toString()));
  }
}
